import type { Work, Service, Gallery, Domain, Sector, Insight, Category, AboutUs, Commitment, Statistics, TeamMember, Newsletter, Owner, NewsletterApiResponse, StatisticsApiResponse, Partner, Testimony } from '../types/api';
import { normalizeRich } from '../utils/text';

class ApiService {
    private baseURL = import.meta.env.VITE_API_BASE_URL || 'https://api.flexgroup.africa/api/v1';

    // Real API methods for services
    private async fetchFromAPI<T>(endpoint: string): Promise<T> {
        try {
            console.log(`🔍 Fetching from API: ${this.baseURL}${endpoint}`);
            const response = await fetch(`${this.baseURL}${endpoint}`);
            console.log(`📡 API Response status: ${response.status} for ${endpoint}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            // Les champs riches saisis dans l'admin arrivent en HTML: on les ramène à du texte structuré
            const data = normalizeRich(await response.json());
            console.log(`✅ API Data received for ${endpoint}:`, data);
            return data;
        } catch (error) {
            console.error(`❌ API Error for ${endpoint}:`, error);
            throw error;
        }
    }

    // Works endpoints - using real API
    async getWorks(params?: {
        service_id?: string;
        show_up?: boolean;
        enable?: boolean;
        limit?: number;
        offset?: number;
    }): Promise<Work[]> {
        console.log('🔍 Fetching works from API with params:', params);
        const works = await this.fetchFromAPI<Work[]>('/works');
        console.log('✅ Works from API:', works);

        // Apply client-side filtering based on params
        let filteredWorks = works;

        if (params?.enable !== undefined) {
            filteredWorks = filteredWorks.filter(work => work.enable === params.enable);
        }

        if (params?.show_up !== undefined) {
            filteredWorks = filteredWorks.filter(work => work.show_up === params.show_up);
        }

        if (params?.service_id) {
            filteredWorks = filteredWorks.filter(work => work.service_id === params.service_id);
        }

        if (params?.limit) {
            filteredWorks = filteredWorks.slice(0, params.limit);
        }

        console.log('✅ Filtered works:', filteredWorks);
        return filteredWorks;
    }

    async getWork(id: string): Promise<Work> {
        console.log(`🔍 Fetching work ${id} from API...`);
        const work = await this.fetchFromAPI<Work>(`/works/${id}`);
        console.log('✅ Work from API:', work);
        return work;
    }

    async createWork(work: Omit<Work, 'id' | 'created_at' | 'updated_at'>): Promise<Work> {
        const response = await fetch(`${this.baseURL}/works`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(work)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

    async updateWork(id: string, workUpdate: Partial<Work>): Promise<Work> {
        const response = await fetch(`${this.baseURL}/works/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(workUpdate)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

    async deleteWork(id: string): Promise<void> {
        const response = await fetch(`${this.baseURL}/works/${id}`, {
            method: 'DELETE'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }

    // Services endpoints - using real API
    async getServices(): Promise<Service[]> {
        console.log('🔍 Fetching services from API...');
        const services = await this.fetchFromAPI<Service[]>('/services');
        console.log('✅ Services from API:', services);
        return services.filter(service => service.enable);
    }

    async getService(id: string): Promise<Service> {
        console.log(`🔍 Fetching service ${id} from API...`);
        const service = await this.fetchFromAPI<Service>(`/services/${id}`);
        console.log('✅ Service from API:', service);
        if (!service.enable) {
            throw new Error(`Service with id ${id} is disabled`);
        }
        return service;
    }

    async getServicesBySector(sectorId: string): Promise<Service[]> {
        console.log(`🔍 Fetching services for sector ${sectorId} from API...`);
        const services = await this.fetchFromAPI<Service[]>(`/sectors/services/${sectorId}`);
        console.log('✅ Services by sector from API:', services);
        return services.filter(service => service.enable);
    }

    async getWorksByService(serviceId: string): Promise<Work[]> {
        console.log(`🔍 Fetching works for service ${serviceId} from API...`);
        const works = await this.fetchFromAPI<Work[]>(`/services/works/${serviceId}`);
        console.log('✅ Works by service from API:', works);
        return works.filter(work => work.enable);
    }

    // Get services for hero section (enabled services only)
    async getHeroServices(): Promise<Service[]> {
        const services = await this.fetchFromAPI<Service[]>('/services');
        return services.filter(service => service.enable);
    }

    // Domains endpoints - using real API
    async getDomains(): Promise<Domain[]> {
        return await this.fetchFromAPI<Domain[]>('/domains');
    }

    async getDomain(id: string): Promise<Domain> {
        return await this.fetchFromAPI<Domain>(`/domains/${id}`);
    }

    // Sectors endpoints - using real API
    async getSectors(): Promise<Sector[]> {
        return await this.fetchFromAPI<Sector[]>('/sectors');
    }

    async getSector(id: string): Promise<Sector> {
        return await this.fetchFromAPI<Sector>(`/sectors/${id}`);
    }

    async getSectorsByDomain(domainId: string): Promise<Sector[]> {
        return await this.fetchFromAPI<Sector[]>(`/domains/sectors/${domainId}`);
    }

    // Commitments endpoints - using real API
    async getCommitments(): Promise<Commitment[]> {
        console.log('Fetching commitments from API...');
        const commitments = await this.fetchFromAPI<Commitment[]>('/commitments');
        console.log('Commitments from API:', commitments);
        return commitments;
    }

    async getCommitment(id: string): Promise<Commitment> {
        return await this.fetchFromAPI<Commitment>(`/commitments/${id}`);
    }

    // Gallery endpoints - using real API
    async getGallery(id: string): Promise<Gallery> {
        console.log(`🔍 Fetching gallery ${id} from API...`);
        const gallery = await this.fetchFromAPI<Gallery>(`/gallery/${id}`);
        console.log('✅ Gallery from API:', gallery);
        return gallery;
    }

    async getGalleries(): Promise<Gallery[]> {
        console.log('🔍 Fetching all galleries from API...');
        const galleries = await this.fetchFromAPI<Gallery[]>('/gallery');
        console.log('✅ Galleries from API:', galleries);
        return galleries;
    }

    async uploadImage(file: File): Promise<Gallery> {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${this.baseURL}/gallery`, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

    // Featured works for homepage
    async getFeaturedWorks(): Promise<Work[]> {
        return this.getWorks({ show_up: true, enable: true, limit: 6 });
    }

    // Portfolio works with pagination
    async getPortfolioWorks(page: number = 1, limit: number = 12): Promise<{
        works: Work[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    }> {
        const offset = (page - 1) * limit;
        const allWorks = await this.getWorks({ enable: true });
        const total = allWorks.length;
        const works = allWorks.slice(offset, offset + limit);
        const totalPages = Math.ceil(total / limit);

        return {
            works,
            total,
            page,
            limit,
            totalPages,
        };
    }

    // Insights methods - using real API
    async getInsights(): Promise<Insight[]> {
        console.log('🔍 Fetching insights from API...');
        const insights = await this.fetchFromAPI<Insight[]>('/insights');
        console.log('✅ Insights from API:', insights);
        return insights.filter(insight => insight.enable);
    }

    async getInsight(id: string): Promise<Insight> {
        console.log(`🔍 Fetching insight ${id} from API...`);
        const insight = await this.fetchFromAPI<Insight>(`/insights/${id}`);
        console.log('✅ Insight from API:', insight);
        if (!insight.enable) {
            throw new Error(`Insight with id ${id} is disabled`);
        }
        return insight;
    }

    async getCategory(id: string): Promise<Category> {
        console.log(`🔍 Fetching category ${id} from API...`);
        const category = await this.fetchFromAPI<Category>(`/categories/${id}`);
        console.log('✅ Category from API:', category);
        return category;
    }

    async getCategories(): Promise<Category[]> {
        console.log('🔍 Fetching all categories from API...');
        const categories = await this.fetchFromAPI<Category[]>('/categories');
        console.log('✅ Categories from API:', categories);
        return categories;
    }

    // About Us methods
    async getAboutUs(): Promise<AboutUs> {
        console.log('🔍 Fetching about us data from API...');
        const aboutUs = await this.fetchFromAPI<AboutUs>('/about-us');
        console.log('✅ About us data from API:', aboutUs);
        return aboutUs;
    }

    async getStatistics(): Promise<Statistics> {
        console.log('🔍 Fetching statistics from API...');
        const stats = await this.fetchFromAPI<StatisticsApiResponse>('/statistics');
        console.log('✅ Statistics from API:', stats);

        return {
            sectors: stats.sectors || 0,
            services: stats.services || 0,
            works: stats.works || 0
        };
    }

    // Team methods - using real API
    async getTeamMembers(): Promise<TeamMember[]> {
        console.log('🔍 Fetching team members from API...');
        const owners = await this.fetchFromAPI<Owner[]>('/owners');
        console.log('✅ Team members from API:', owners);

        // Map Owner data to TeamMember format
        const teamMembers: TeamMember[] = owners
            .filter(owner => owner.enable)
            .map(owner => ({
                id: owner.id,
                name: owner.name,
                position: owner.poste,
                bio: owner.description,
                image: owner.image,
                social_links: {
                    linkedin: owner.linkedin,
                    twitter: owner.twitter,
                    email: owner.email,
                    facebook: owner.website
                },
                enable: owner.enable
            }));

        return teamMembers;
    }

    async getTeamMember(id: string): Promise<TeamMember | null> {
        const owner = await this.fetchFromAPI<Owner>(`/owners/${id}`);
        if (!owner.enable) return null;

        // Map Owner data to TeamMember format
        const teamMember: TeamMember = {
            id: owner.id,
            name: owner.name,
            position: owner.poste,
            bio: owner.description,
            image: owner.image,
            social_links: {
                linkedin: owner.linkedin,
                twitter: owner.twitter,
                email: owner.email,
                facebook: owner.website
            },
            enable: owner.enable
        };

        return teamMember;
    }

    // Newsletter methods - using real API
    async getNewsletter(): Promise<Newsletter> {
        console.log('🔍 Fetching newsletter data from API...');
        const newsletters = await this.fetchFromAPI<NewsletterApiResponse[]>('/news-letters');
        console.log('✅ Newsletter data from API:', newsletters);

        // Return the first newsletter or create a default one
        if (newsletters && newsletters.length > 0) {
            const newsletter = newsletters[0];
            return {
                id: newsletter.id,
                title: newsletter.title || "Restez informé de nos dernières actualités",
                description: newsletter.message || "Recevez nos dernières actualités, projets et insights directement dans votre boîte mail. Pas de spam, juste du contenu de qualité.",
                placeholder: "Votre adresse email",
                button_text: "S'inscrire",
                enable: true
            };
        }

        // Default newsletter data if API returns empty
        return {
            id: "default-newsletter",
            title: "Restez informé de nos dernières actualités",
            description: "Recevez nos dernières actualités, projets et insights directement dans votre boîte mail. Pas de spam, juste du contenu de qualité.",
            placeholder: "Votre adresse email",
            button_text: "S'inscrire",
            enable: true
        };
    }

    async subscribeNewsletter(email: string): Promise<void> {
        console.log('🔍 Subscribing to newsletter via API...');

        const subscriberData = {
            email: email,
            navigator: navigator.userAgent || 'Unknown'
        };

        const response = await fetch(`${this.baseURL}/subscriber`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(subscriberData)
        });

        console.log('📡 Newsletter subscription response status:', response.status);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ Newsletter subscription successful:', result);
    }

    // Partners endpoints
    async getPartners(): Promise<Partner[]> {
        const partners = await this.fetchFromAPI<Partner[]>('/partners');
        return partners.filter(p => p.enable);
    }

    // Testimonies endpoints
    async getTestimonies(): Promise<Testimony[]> {
        const testimonies = await this.fetchFromAPI<Testimony[]>('/testimonies');
        return testimonies.filter(t => t.enable);
    }
}

export const apiService = new ApiService();
export default apiService;
